import { sb, SUPABASE_ANON_KEY, SUPABASE_FUNCTIONS_URL } from '../../supabase';

export const MAABAR_AI_PERSONA_NAME = 'وكيل معبر';
export const MAABAR_AI_FUNCTION_NAME = process.env.REACT_APP_MAABAR_AI_FUNCTION_NAME || 'maabar-ai';
export const MAABAR_AI_MODEL = 'gemini-2.0-flash';
// Anonymous headers. Kept as the floor, not as the default: three tasks are
// reachable logged out and must keep working with nothing but the anon key —
// idea_to_product and product_conversation from IdeaToProduct on the public
// home page (its login gate is in submitRequest, AFTER both AI calls), and
// customer_support from the /support widget.
export const MAABAR_AI_HEADERS = {
  'Content-Type': 'application/json',
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  apikey: SUPABASE_ANON_KEY,
};

/**
 * Headers for a maabar-ai call, carrying the caller's own identity when they
 * have one.
 *
 * The anon key is a JWT the whole internet holds, so every signed-in user shared
 * one bucket with every visitor: maabar-ai could only rate-limit by IP, and
 * factories behind one industrial-park egress throttled each other. Sending the
 * session token gives the function a real `sub` to key on.
 *
 * `apikey` stays the anon key regardless — that header is the API gateway's, not
 * the user's, and Supabase requires it either way.
 *
 * Falls back to the anon key whenever there is no session, so the three public
 * tasks above are unaffected. getSession() refreshes an expired token on its
 * own; if it throws or returns nothing we send the anon key rather than an
 * expired bearer, because a stale token is a 401 where the anon key is a 200.
 */
export async function maabarAiHeaders() {
  try {
    const { data } = await sb.auth.getSession();
    const token = data?.session?.access_token;
    if (token) {
      return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON_KEY };
    }
  } catch { /* no session, or storage unavailable → anonymous */ }
  return MAABAR_AI_HEADERS;
}
export const MAABAR_AI_ENDPOINT = `${SUPABASE_FUNCTIONS_URL}/${MAABAR_AI_FUNCTION_NAME}`;

export const MAABAR_AI_TASKS = {
  IDEA_TO_PRODUCT: 'idea_to_product',
  PRODUCT_CONVERSATION: 'product_conversation',
  CHAT_TRANSLATION: 'chat_translation',
  CUSTOMER_SUPPORT: 'customer_support',
  HS_CODE_SUGGEST: 'hs_code_suggest',
  COMPANY_ROMANIZATION: 'company_romanization',
};

export const MAABAR_AI_SUPPORT_CHANNELS = {
  email: 'support@maabar.io',
  whatsapp: 'https://wa.me/966554064679',
};

export const MAABAR_AI_SUPPORT_PROMISE = {
  ar: 'دعم معبر 24/7 للتوجيه السريع، مشاكل الحسابات، الطلبات، الدفع، الموردين، والترجمة التجارية.',
  en: 'Maabar support is available 24/7 for quick guidance across accounts, orders, payments, suppliers, and trade translation.',
  zh: 'Maabar 提供 24/7 支持，可处理账户、订单、付款、供应商与商务翻译相关问题。',
};

export const MAABAR_AI_SAUDI_REP_NAMES = [
  { name: 'سلمان', gender: 'male' },
  { name: 'تركي', gender: 'male' },
  { name: 'فيصل', gender: 'male' },
  { name: 'سعود', gender: 'male' },
  { name: 'عبدالعزيز', gender: 'male' },
  { name: 'نورة', gender: 'female' },
  { name: 'الجوهرة', gender: 'female' },
  { name: 'سارة', gender: 'female' },
  { name: 'ريم', gender: 'female' },
  { name: 'هيا', gender: 'female' },
];

const SESSION_REP_KEY = 'maabar_ai_saudi_rep';
const REP_GENDER_TOGGLE_KEY = 'maabar_ai_saudi_rep_gender_toggle';

export const TRANSLATION_DIRECTIONS = [
  { id: 'off', source: null, target: null },
  { id: 'ar_to_zh', source: 'ar', target: 'zh' },
  { id: 'zh_to_ar', source: 'zh', target: 'ar' },
  { id: 'ar_to_en', source: 'ar', target: 'en' },
  { id: 'en_to_ar', source: 'en', target: 'ar' },
  { id: 'zh_to_en', source: 'zh', target: 'en' },
  { id: 'en_to_zh', source: 'en', target: 'zh' },
];

export function getDefaultTranslationDirection(appLang = 'ar') {
  if (appLang === 'zh') return 'ar_to_zh';
  if (appLang === 'en') return 'ar_to_en';
  return 'zh_to_ar';
}

/**
 * Infer translation direction from both parties' languages.
 * Translates FROM the partner's language TO the current user's language.
 * Returns 'off' when both speak the same language.
 */
export function inferTranslationDirection(myLang = 'ar', partnerLang = null) {
  if (!partnerLang || myLang === partnerLang) return 'off';
  const directionId = `${partnerLang}_to_${myLang}`;
  return TRANSLATION_DIRECTIONS.find((d) => d.id === directionId) ? directionId : 'off';
}

// Which of the three languages a string is written in, by script. Arabic and
// Chinese have disjoint Unicode blocks, so this is exact for them; everything
// else is treated as English.
//
// It exists because maabar-ai REQUIRES sourceLanguage — it answers 400 without
// one, so there is no "auto" to lean on — and profiles.lang cannot be trusted to
// be present. Reading the script is more reliable than guessing from a role: a
// Chinese supplier writing English gets 'en', which is what we want.
export function detectTextLanguage(text = '') {
  const s = String(text || '');
  if (/[؀-ۿݐ-ݿ]/.test(s)) return 'ar';
  if (/[一-鿿㐀-䶿]/.test(s)) return 'zh';
  return 'en';
}

// A direction id from a known source and target, or 'off' when they match or the
// pair is not one of the six we support.
export function directionBetween(sourceLang, targetLang) {
  if (!sourceLang || !targetLang || sourceLang === targetLang) return 'off';
  const id = `${sourceLang}_to_${targetLang}`;
  return TRANSLATION_DIRECTIONS.find((d) => d.id === id) ? id : 'off';
}

// Conversation direction with a fallback for the (currently universal) case
// where the partner's profiles.lang is null: read the partner's own words
// instead. `sampleText` should be their most recent incoming message.
//
// This is a backstop, not the fix. profiles.lang also drives email language, so
// a client-side fallback repairs chat and leaves email wrong — the column itself
// still needs to be populated.
export function inferTranslationDirectionFrom(myLang = 'ar', partnerLang = null, sampleText = '') {
  const known = inferTranslationDirection(myLang, partnerLang);
  if (known !== 'off') return known;
  if (partnerLang) return 'off';                    // genuinely the same language
  if (!String(sampleText || '').trim()) return 'off';
  return directionBetween(detectTextLanguage(sampleText), myLang);
}

export function getTranslationDirection(directionId) {
  return TRANSLATION_DIRECTIONS.find((direction) => direction.id === directionId) || TRANSLATION_DIRECTIONS[0];
}

export function getDirectionLabel(directionId, lang = 'ar') {
  const labels = {
    ar: {
      off: 'بدون ترجمة',
      ar_to_zh: 'عربي ← صيني',
      zh_to_ar: 'صيني ← عربي',
      ar_to_en: 'عربي ← إنجليزي',
      en_to_ar: 'إنجليزي ← عربي',
      zh_to_en: 'صيني ← إنجليزي',
      en_to_zh: 'إنجليزي ← صيني',
    },
    en: {
      off: 'No translation',
      ar_to_zh: 'Arabic ← Chinese',
      zh_to_ar: 'Chinese ← Arabic',
      ar_to_en: 'Arabic ← English',
      en_to_ar: 'English ← Arabic',
      zh_to_en: 'Chinese ← English',
      en_to_zh: 'English ← Chinese',
    },
    zh: {
      off: '关闭翻译',
      ar_to_zh: '阿拉伯语 ← 中文',
      zh_to_ar: '中文 ← 阿拉伯语',
      ar_to_en: '阿拉伯语 ← 英语',
      en_to_ar: '英语 ← 阿拉伯语',
      zh_to_en: '中文 ← 英语',
      en_to_zh: '英语 ← 中文',
    },
  };

  return labels[lang]?.[directionId] || labels.ar[directionId] || directionId;
}

function pickSaudiRepresentative() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return MAABAR_AI_SAUDI_REP_NAMES[0];
  }

  const lastGender = window.localStorage.getItem(REP_GENDER_TOGGLE_KEY);
  const nextGender = lastGender === 'female' ? 'male' : 'female';
  const candidates = MAABAR_AI_SAUDI_REP_NAMES.filter((representative) => representative.gender === nextGender);
  const selected = candidates[Math.floor(Math.random() * candidates.length)] || MAABAR_AI_SAUDI_REP_NAMES[0];
  window.localStorage.setItem(REP_GENDER_TOGGLE_KEY, nextGender);
  return selected;
}

export function getSessionSaudiRepresentative() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return MAABAR_AI_SAUDI_REP_NAMES[0];
  }

  const stored = window.sessionStorage.getItem(SESSION_REP_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (_error) {
      window.sessionStorage.removeItem(SESSION_REP_KEY);
    }
  }

  const selected = pickSaudiRepresentative();
  window.sessionStorage.setItem(SESSION_REP_KEY, JSON.stringify(selected));
  return selected;
}

export function getSaudiRepresentativeIntro(lang = 'ar') {
  const representative = getSessionSaudiRepresentative();

  if (lang === 'zh') {
    return `您好，我是 ${representative.name}，来自 Maabar。`;
  }

  if (lang === 'en') {
    return `Hello, this is ${representative.name} from Maabar.`;
  }

  return `مرحبا، معك ${representative.name} من معبر.`;
}
